/* @flow */
/**
 * Subject type: subjects are collections belonging to the global config, used to define a type. They are made available in flows for input types.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * @typedef { Object } Subject
 * @memberof SubjectService
 */
import Typenames from './Typenames'
import { ActionTypes, store } from 'store'

export type Subject = {
  name: string,
  version?: string,
  key: string,
  regex: string
}

const TYPENAME = Typenames.SUBJECT

/**
 * Creates a new subject.
 * @function
 * @memberof SubjectService
 *
 * @return { Promise<string> } The id of the added subject.
 */
const createSubject = (subject: Subject): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: subject })
}

/**
 * Reads a subject, flattening 'descriptive'.
 * @function
 * @memberof SubjectService
 *
 * @return { Promise<Subject> } The subject.
 */
const readSubject = (id: string): Promise<Subject> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
}

/**
 * Updates a subject.
 * @function
 * @memberof SubjectService
 *
 * @return { Promise<any> }
 */
const updateSubject = (id: string, subject: Subject): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, { typename: TYPENAME, id: id, item: subject })
}

/**
 * Deletes a subject.
 * @function
 * @memberof SubjectService
 *
 * @return { Promise<any> }
 */
const deleteSubject = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all subjects flattening descriptive.
 * @function
 * @memberof SubjectService
 *
 * @return { Promise<Array<Subject>> } All the subjects.
 */
const readAllSubjects = (): Promise<Array<Subject>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME)
}

const self = {
  createSubject,
  readSubject,
  updateSubject,
  deleteSubject,
  readAllSubjects
}

/**
 * Subject service: CRUD interface for subjects.
 * @module SubjectService
 * @namespace SubjectService
 * @version 1.0.0
 */
export default self
